import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useConfirm } from 'material-ui-confirm';

import {
  approvePaymentAction,
  deletePaymentAction,
  payPaymentAction,
  resetPaymentProcessAction,
  startPaymentApprovalFlowAction,
  updatePaymentTagsAction,
} from '@/domain/payments';

import { PAYMENT_TAGS } from '@/domain/payments/constants';
import { formatAmount } from '@/lib/formatAmount';

const useContextMenuActions = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const confirm = useConfirm();

  const handleStartApprovalFlow = useCallback(
    (id: string) => {
      confirm({
        title: formatMessage({
          id: 'payment.confirmation.sendToApproval.title',
          defaultMessage: 'Send payment to approval',
        }),
        description: formatMessage({
          id: 'payment.confirmation.sendToApproval.placeholder',
          defaultMessage: 'Are you sure?',
        }),
        confirmationText: formatMessage({
          id: 'payment.footer.actionButton.sendToApproval',
          defaultMessage: 'Send to approval',
        }),
      })
        .then(() => {
          dispatch(startPaymentApprovalFlowAction({ id }));
        })
        .catch(() => {});
    },
    [dispatch, confirm, formatMessage],
  );

  const handleApprove = useCallback(
    (id: string) => {
      confirm({
        title: formatMessage({
          id: 'payment.confirmation.sendToApproval.title',
          defaultMessage: 'Send payment to approval',
        }),
        description: formatMessage({
          id: 'payment.confirmation.sendToApproval.placeholder',
          defaultMessage: 'Are you sure?',
        }),
        confirmationText: formatMessage({
          id: 'payment.footer.actionButton.approve',
          defaultMessage: 'Approve',
        }),
      })
        .then(() => {
          dispatch(approvePaymentAction({ id }));
        })
        .catch(() => {});
    },
    [dispatch, confirm, formatMessage],
  );

  const handleResetApprovalFlow = useCallback(
    (id: string) => {
      confirm({
        title: formatMessage({
          id: 'payment.confirmation.resetProcess.title',
          defaultMessage: 'Reset the process',
        }),
        description: formatMessage({
          id: 'payment.confirmation.resetProcess.placeholder',
          defaultMessage: 'Are you sure?',
        }),
        confirmationText: formatMessage({
          id: 'payment.footer.actionButton.resetProcess',
          defaultMessage: 'Reset the process',
        }),
      })
        .then(() => {
          dispatch(resetPaymentProcessAction({ id }));
        })
        .catch(() => {});
    },
    [dispatch, confirm, formatMessage],
  );

  const handlePay = useCallback(
    (id: string, totalAmount: string, currencyCode?: string | null) => {
      confirm({
        title: formatMessage({
          id: 'payment.confirmation.pay.title',
          defaultMessage: 'Pay payment',
        }),
        description: formatMessage({
          id: 'payment.confirmation.pay.placeholder',
          defaultMessage: 'Are you sure?',
        }),
        confirmationText: formatMessage(
          { id: 'payment.footer.actionButton.pay', defaultMessage: 'Pay {sum}' },
          { sum: formatAmount(totalAmount, currencyCode) },
        ),
      })
        .then(() => {
          dispatch(payPaymentAction({ id }));
        })
        .catch(() => {});
    },
    [dispatch, confirm, formatMessage],
  );

  const handleDelete = useCallback(
    (id: string) => {
      confirm({
        title: formatMessage({
          id: 'payment.confirmation.delete.title',
          defaultMessage: 'Delete payment',
        }),
        description: formatMessage({
          id: 'payment.confirmation.delete.placeholder',
          defaultMessage: 'Are you sure?',
        }),
        confirmationText: formatMessage({
          id: 'button.delete',
          defaultMessage: 'Delete',
        }),
        confirmationButtonProps: {
          color: 'error',
        },
      })
        .then(() => {
          dispatch(deletePaymentAction({ id }));
        })
        .catch(() => {});
    },
    [dispatch, confirm, formatMessage],
  );

  const updateTag = useCallback(
    (id: string, tag: string, isActive = true) => {
      dispatch(updatePaymentTagsAction({ id, tag, isActive }));
    },
    [dispatch],
  );

  const handleArchive = useCallback(
    (id: string) => {
      updateTag(id, PAYMENT_TAGS.archive);
    },
    [updateTag],
  );

  const handleUnarchive = useCallback(
    (id: string) => {
      updateTag(id, PAYMENT_TAGS.archive, false);
    },
    [updateTag],
  );

  const handleSaveForLater = useCallback(
    (id: string) => {
      updateTag(id, PAYMENT_TAGS.saveForLater);
    },
    [updateTag],
  );

  const handleStartPaymentProcess = useCallback(
    (id: string) => {
      updateTag(id, PAYMENT_TAGS.saveForLater, false);
    },
    [updateTag],
  );

  return {
    handleStartApprovalFlow,
    handleArchive,
    handleSaveForLater,
    handleUnarchive,
    handleStartPaymentProcess,
    handleApprove,
    handleResetApprovalFlow,
    handlePay,
    handleDelete,
  };
};

export default useContextMenuActions;
